import { useCallback, useState } from "react";

import { useDropzone } from "react-dropzone";

import { Box, Column, Text, CheckIcon, FormField, Row } from "@hightouch/htui";

interface Props {
  /** Description to show within uploader type  */
  descripion?: string;
  /**
   * List of accepted file types.
   * If `undefined`, will accept all file types
   */
  acceptedFileTypes?: string[];

  /**
   * Function to transform file bolb into value,
   * "base64" : Will transform your file into a base64 string
   * "JSONParse": Will parse your JSON file into an object
   *  Custom Function: Will give you the file to return any value format
   */
  transformation: "base64" | "JSONParse" | "string" | ((blob: File) => any);
  label?: string;
  value: string;
  onChange?: (value: any) => void;
}

export const FileUploader = ({ transformation, onChange, ...props }: Props) => {
  const [error, setError] = useState<string>("");
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>(
    undefined,
  );

  const { getRootProps, getInputProps } = useDropzone({
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    onDrop: useCallback(async (acceptedFiles) => {
      setError("");
      const file = acceptedFiles[0];
      if (!file) {
        return setUploadedFileName(undefined);
      }

      setUploadedFileName(file.name);
      if (transformation === "JSONParse") {
        const fileContents = await file.text();
        try {
          if (typeof onChange === "function") {
            onChange(JSON.parse(fileContents));
          }
        } catch (_err) {
          setError("Couldn't parse file. Make sure it's valid JSON.");
        }
      } else if (transformation === "base64") {
        if (typeof onChange === "function") {
          onChange(await fileToBase64(file));
        }
      } else if (transformation === "string") {
        if (typeof onChange === "function") {
          const res = await fileToString(file);
          onChange(res);
        }
      } else {
        const transformedValue = transformation(file);

        if (typeof onChange === "function") {
          onChange(transformedValue);
        }
      }
    }, []),
    accept:
      Array.isArray(props.acceptedFileTypes) &&
      props.acceptedFileTypes.length > 0
        ? props.acceptedFileTypes
        : undefined,
  });

  return (
    <FormField error={error} label={props.label ?? ""}>
      <Column
        alignItems="center"
        textAlign="center"
        padding="20px"
        borderWidth={1}
        borderRadius={2}
        borderColor={uploadedFileName ? "success.base" : "base.border"}
        borderStyle="dashed"
        backgroundColor={
          uploadedFileName ? "success.background" : "base.background"
        }
        color="text.tertiary"
        outline="none"
        transitionProperty="border-color, background-color"
        transitionDuration="slow"
        transitionTimingFunction="easeOutQuad"
      >
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          {!uploadedFileName ? (
            <>
              {props.value && (
                <Text fontWeight="semibold" mb={2}>
                  Using last saved file
                </Text>
              )}

              <Text>
                Drag your file here, or click to select a file
                {props.value && " to overwrite the current file"}.{" "}
              </Text>
              {Array.isArray(props.acceptedFileTypes) &&
                props.acceptedFileTypes.length > 0 && (
                  <Text>
                    Accepted file types: {props.acceptedFileTypes.join(", ")}
                  </Text>
                )}
            </>
          ) : (
            <Row alignItems="center">
              <Text fontWeight="semibold">
                Uploaded file: {uploadedFileName}
              </Text>
              <Box
                as={CheckIcon}
                color="success.base"
                fontSize="18px"
                transitionProperty="common"
                transitionDuration="fast"
                transitionTimingFunction="easeOutQuad"
              />
            </Row>
          )}
        </div>
      </Column>
    </FormField>
  );
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () =>
      resolve(reader.result?.toString().split(",")[1] || "");
    reader.onerror = (error) => reject(error);
  });
};

export const fileToString = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      resolve(reader.result?.toString() || "");
    };
    // A real Error rather than the ProgressEvent FileReader hands back: Sentry
    // files a non-Error as "Non-Error exception captured with keys: isTrusted"
    // and groups every read failure in the app into one unusable issue.
    reader.onerror = () =>
      reject(new Error(`Couldn't read the file "${file.name}"`));
  });
};
